import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { clearSessionHints, freeFetch } from './api';

// status: 'checking' | 'in' | 'out'
const FreeAuthContext = createContext({ status: 'checking', email: '' });

export const useFreeAuth = () => useContext(FreeAuthContext);

export function FreeAuthProvider({ children }) {
  const [state, setState] = useState({
    status: localStorage.getItem('free_signed_in') ? 'in' : 'checking',
    email: localStorage.getItem('free_email') || '',
  });
  // Guards against the in-flight session check landing after a magic-link
  // verify and wrongly signing the user straight back out.
  const justSignedIn = useRef(false);
  // Stops a session check that started before sign-out from signing the user back in.
  const signedOut = useRef(false);

  const markSignedIn = useCallback((email) => {
    justSignedIn.current = true;
    signedOut.current = false;
    localStorage.setItem('free_signed_in', '1');
    localStorage.setItem('free_email', email || '');
    localStorage.removeItem('free_token');
    setState({ status: 'in', email: email || '' });
  }, []);

  const signOut = useCallback(async () => {
    justSignedIn.current = false;
    signedOut.current = true;
    try {
      await freeFetch('/auth/logout', { method: 'POST' });
    } catch (e) {
      /* cookie is cleared server-side on the next session check regardless */
    }
    clearSessionHints();
    setState({ status: 'out', email: '' });
  }, []);

  // For when the server has already ended the session (e.g. account deleted).
  const markSignedOut = useCallback(() => {
    justSignedIn.current = false;
    signedOut.current = true;
    clearSessionHints();
    setState({ status: 'out', email: '' });
  }, []);

  useEffect(() => {
    freeFetch('/auth/session')
      .then((data) => {
        if (justSignedIn.current || signedOut.current) return;
        if (data.authenticated) {
          markSignedIn(data.email);
        } else {
          clearSessionHints();
          setState({ status: 'out', email: '' });
        }
      })
      .catch(() => {
        if (justSignedIn.current) return;
        setState((prev) => (prev.status === 'in' ? prev : { status: 'out', email: '' }));
      });
  }, [markSignedIn]);

  return (
    <FreeAuthContext.Provider value={{ ...state, markSignedIn, signOut, markSignedOut }}>
      {children}
    </FreeAuthContext.Provider>
  );
}
